<template>
  <div class="wizard-ai-chat">
    <div class="wizard-ai-chat-header">
      <span class="wizard-ai-chat-icon">&#9733;</span> AI-помощник
      <span
        class="wizard-ai-chat-status"
        :class="connected ? 'wizard-ai-chat-status--on' : ''"
      >{{ connected ? 'online' : 'offline' }}</span>
    </div>
    <div
      ref="chatLog"
      class="wizard-ai-chat-log"
      :class="{ 'wizard-ai-chat-log--tall': tall }"
    >
      <div
        v-if="messages.length === 0"
        class="wizard-ai-chat-hint"
      >
        {{ hint }}
      </div>
      <div
        v-for="(msg, i) in messages"
        :key="keyPrefix + i"
        class="wizard-ai-chat-msg"
        :class="msg.sender === 'user' ? 'wizard-ai-chat-msg--user' : 'wizard-ai-chat-msg--bot'"
      >
        <div
          v-if="msg.format === 'markdown'"
          class="wizard-ai-chat-md"
          v-html="sanitizeHtml(msg.text)"
        />
        <template v-else>
          {{ msg.text }}
        </template>
      </div>
      <div
        v-if="waiting"
        class="wizard-ai-chat-msg wizard-ai-chat-msg--bot wizard-ai-chat-typing"
      >
        <span /><span /><span />
      </div>
    </div>
    <form
      class="wizard-ai-chat-input"
      @submit.prevent="$emit('send')"
    >
      <input
        :value="inputText"
        type="text"
        placeholder="Сообщение ИИ..."
        :disabled="!connected"
        @input="$emit('update:inputText', $event.target.value)"
      >
      <button
        type="submit"
        :disabled="!inputText || !inputText.trim() || !connected"
      >
        &#9654;
      </button>
    </form>
  </div>
</template>

<script>
import DOMPurify from 'dompurify'
import { marked } from 'marked'

export default {
  name: 'WizardAiChat',
  props: {
    messages: { type: Array, default: () => [] },
    inputText: { type: String, default: '' },
    connected: { type: Boolean, default: false },
    waiting: { type: Boolean, default: false },
    hint: { type: String, default: 'Задайте вопрос ИИ...' },
    tall: { type: Boolean, default: false },
    keyPrefix: { type: String, default: 'ai-' },
  },
  methods: {
    sanitizeHtml(text) {
      if (!text) return ''
      return DOMPurify.sanitize(marked.parse(text))
    },
    scrollToBottom() {
      const el = this.$refs.chatLog
      if (el) el.scrollTop = el.scrollHeight
    },
  },
}
</script>

<style lang="scss" scoped>
.wizard-ai-chat {
  display: flex;
  flex-direction: column;
  background: #fafaff;
  border: 1px solid #e0dff5;
  border-radius: 8px;
  overflow: hidden;
  margin-top: 10px;
  max-height: 400px;
}

.wizard-ai-chat-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  background: #7367f0;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.wizard-ai-chat-icon {
  font-size: 14px;
}

.wizard-ai-chat-status {
  margin-left: auto;
  font-size: 10px;
  font-weight: 400;
  color: rgba(255,255,255,0.6);
  &--on { color: #a5d6a7; }
}

.wizard-ai-chat-log {
  flex: 1;
  min-height: 120px;
  max-height: 180px;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.wizard-ai-chat-log--tall {
  min-height: 200px;
  max-height: 340px;
}

.wizard-ai-chat-hint {
  font-size: 11px;
  color: #b9b9c3;
  text-align: center;
  padding: 12px 4px;
  line-height: 1.4;
}

.wizard-ai-chat-msg {
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.4;
  max-width: 95%;
  word-wrap: break-word;

  &--user {
    background: #7367f0;
    color: #fff;
    align-self: flex-end;
    border-bottom-right-radius: 2px;
  }

  &--bot {
    background: #fff;
    color: #5e5873;
    border: 1px solid #ebe9f1;
    align-self: flex-start;
    border-bottom-left-radius: 2px;
  }
}

.wizard-ai-chat-md {
  font-size: 12px;
  line-height: 1.4;

  ::v-deep p { margin: 0 0 4px; }
  ::v-deep code { background: #f0eef8; padding: 1px 4px; border-radius: 3px; font-size: 11px; }
  ::v-deep pre { background: #f0eef8; padding: 6px; border-radius: 4px; overflow-x: auto; font-size: 11px; margin: 4px 0; }
  ::v-deep ul, ::v-deep ol { margin: 2px 0; padding-left: 16px; }
}

.wizard-ai-chat-typing {
  display: flex;
  gap: 4px;
  padding: 8px 12px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #b9b9c3;
    animation: ai-dot-bounce 1.2s infinite;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes ai-dot-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
}

.wizard-ai-chat-input {
  display: flex;
  border-top: 1px solid #ebe9f1;

  input {
    flex: 1;
    border: none;
    padding: 8px 10px;
    font-size: 12px;
    outline: none;
    background: #fff;
    min-width: 0;

    &::placeholder { color: #d0d0d0; }
    &:disabled { background: #f5f5f5; }
  }

  button {
    border: none;
    background: #7367f0;
    color: #fff;
    padding: 8px 12px;
    font-size: 13px;
    cursor: pointer;
    transition: background 0.15s;

    &:hover:not(:disabled) { background: #5e50ee; }
    &:disabled { background: #c8c4f0; cursor: default; }
  }
}
</style>
